/**
 * Colour grading wheels.
 *
 * A wheel is two numbers, which way to push the colour and how far, so the
 * puck's angle and distance are the whole control. The disc is painted with the
 * same hue ramp the renderer uses to build the tint, so the colour under the
 * puck is the colour that lands on the picture, and the degrees and strength
 * are printed underneath because "somewhere in the orange" is not a setting you
 * can repeat on the next frame.
 *
 * The rail is resizable, and three discs across a 260px rail leaves each one
 * under 70px: the puck spills into the gap between columns and the light slider
 * beneath it has nothing to work with. So the block measures its own column and
 * drops to one zone at a time rather than shrinking past the point of use.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { GradingWheel, GradingZone } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../state/store.ts';
import { Slider } from './controls/Slider.tsx';
import { Segmented } from './controls/Panel.tsx';
import { Tip } from './controls/Tooltip.tsx';

/** Neutral centre of the disc, in 0..1 linear-ish display values. */
const CENTRE: [number, number, number] = [0.105, 0.095, 0.088];
/** How far the rim goes toward a fully saturated hue. */
const RIM = 0.92;

/**
 * Half the puck, in CSS pixels. `.wheel-puck` is a 9px border-box square, and
 * everything is box-sizing: border-box, so the 1.5px ring is inside that. The
 * extra pixel keeps the ring off the disc's own border at full strength.
 */
const PUCK_REACH_MARGIN = 5.5;

/** Below this column width the three-up grid stops being usable. */
const WIDE_MIN = 300;
/** Slack on the way back, so dragging the rail past the threshold cannot flap. */
const WIDE_HYSTERESIS = 18;

/** Same construction as the renderer's `hueToRgb`, so disc and output agree. */
function hueRgb(degrees: number): [number, number, number] {
  const h = (((degrees % 360) + 360) % 360) / 60;
  const x = 1 - Math.abs((h % 2) - 1);
  if (h < 1) return [1, x, 0];
  if (h < 2) return [x, 1, 0];
  if (h < 3) return [0, 1, x];
  if (h < 4) return [0, x, 1];
  if (h < 5) return [x, 0, 1];
  return [1, 0, x];
}

function swatch(hue: number, saturation: number): string {
  const [r, g, b] = hueRgb(hue);
  const mix = Math.pow(Math.min(1, Math.max(0, saturation / 100)), 0.85) * RIM;
  const channel = (value: number, base: number) => Math.round(255 * (base + (value - base) * mix));
  return `rgb(${channel(r, CENTRE[0])}, ${channel(g, CENTRE[1])}, ${channel(b, CENTRE[2])})`;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Tracks an element's CSS-pixel box; takes the element so it survives remounts.
 * Measured off the bounding rect rather than clientWidth: the rail is dragged
 * to fractional widths, and rounding the box before scaling it by the device
 * ratio is what makes a canvas resample and go soft.
 */
function useElementSize<T extends HTMLElement>(element: T | null) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize((previous) =>
        previous.width === rect.width && previous.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return size;
}

export interface ColorWheelProps {
  /** Zone name shown under the disc, e.g. "Shadows". */
  name: string;
  value: GradingWheel;
  /** One sentence, shown on hover or focus of the name. */
  help?: string;
  /** Short label for the luminance slider; the column can be under 100px. */
  luminanceLabel?: string;
  luminanceHelp?: string;
  /** Ceiling on the disc so it never outgrows, or overflows, its column. */
  maxDisc?: number;
  onChange: (next: GradingWheel) => void;
  /** Fired on pointer or key release, the point at which history is written. */
  onCommit: () => void;
}

export function ColorWheel({
  name,
  value,
  help,
  luminanceLabel = 'Light',
  luminanceHelp,
  maxDisc = 132,
  onChange,
  onCommit,
}: ColorWheelProps) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const size = useElementSize(canvas);
  const [dragging, setDragging] = useState(false);

  const paint = useCallback(() => {
    if (!canvas || size.width === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.max(1, Math.round(size.width * dpr));
    if (canvas.width !== px) canvas.width = px;
    if (canvas.height !== px) canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = ctx.createImageData(px, px);
    const data = image.data;
    const radius = px / 2;

    for (let y = 0; y < px; y += 1) {
      const dy = (y + 0.5 - radius) / radius;
      for (let x = 0; x < px; x += 1) {
        const dx = (x + 0.5 - radius) / radius;
        const distance = Math.hypot(dx, dy);
        if (distance > 1 + 1 / radius) continue;

        let hue = (Math.atan2(-dy, dx) * 180) / Math.PI;
        if (hue < 0) hue += 360;
        const [r, g, b] = hueRgb(hue);
        const mix = Math.pow(Math.min(1, distance), 0.85) * RIM;
        const index = (y * px + x) * 4;
        data[index] = Math.round(255 * (CENTRE[0] + (r - CENTRE[0]) * mix));
        data[index + 1] = Math.round(255 * (CENTRE[1] + (g - CENTRE[1]) * mix));
        data[index + 2] = Math.round(255 * (CENTRE[2] + (b - CENTRE[2]) * mix));
        // One-pixel alpha ramp at the rim: the disc has to read as a circle.
        data[index + 3] = Math.round(255 * clamp((1 - distance) * radius + 0.5, 0, 1));
      }
    }
    ctx.putImageData(image, 0, 0);

    // A centre mark, so "no grade" is a visible position rather than a guess.
    ctx.strokeStyle = 'rgba(255, 237, 215, 0.3)';
    ctx.lineWidth = Math.max(1, Math.round(dpr * 0.5));
    const arm = Math.max(2, Math.round(radius * 0.09));
    ctx.beginPath();
    ctx.moveTo(radius - arm, radius);
    ctx.lineTo(radius + arm, radius);
    ctx.moveTo(radius, radius - arm);
    ctx.lineTo(radius, radius + arm);
    ctx.stroke();
  }, [canvas, size]);

  useEffect(() => {
    paint();
  }, [paint]);

  const applyFromPointer = (clientX: number, clientY: number) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const radius = rect.width / 2;
    if (radius <= 0) return;
    const dx = clientX - rect.left - radius;
    const dy = clientY - rect.top - radius;
    const distance = Math.hypot(dx, dy);
    const hue = distance < 0.5 ? value.hue : (((Math.atan2(-dy, dx) * 180) / Math.PI) + 360) % 360;
    onChange({
      ...value,
      hue: Math.round(hue),
      saturation: Math.round(clamp((distance / radius) * 100, 0, 100)),
    });
  };

  const clearZone = () => {
    onChange({ hue: 0, saturation: 0, luminance: 0 });
    onCommit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const hueStep = event.shiftKey ? 15 : 3;
    const satStep = event.shiftKey ? 10 : 2;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onChange({ ...value, hue: (value.hue + hueStep) % 360 });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onChange({ ...value, hue: (value.hue - hueStep + 360) % 360 });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      onChange({ ...value, saturation: clamp(value.saturation + satStep, 0, 100) });
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      onChange({ ...value, saturation: clamp(value.saturation - satStep, 0, 100) });
    } else if (event.key === 'Home') {
      event.preventDefault();
      clearZone();
    }
  };

  const angle = (value.hue * Math.PI) / 180;
  // Reach is a percentage of the disc, so the puck's own radius has to come out
  // of it or full strength hangs the ring over the rim and into the next column.
  const maxReach = size.width > 0 ? Math.max(20, 50 - (PUCK_REACH_MARGIN / size.width) * 100) : 44;
  const reach = (clamp(value.saturation, 0, 100) / 100) * maxReach;
  const readout = value.saturation === 0 ? 'Neutral' : `${Math.round(value.hue)}° · ${value.saturation}`;

  return (
    <div className="wheel" role="group" aria-label={`${name} colour grading`}>
      <div
        className="wheel-disc"
        style={{ maxWidth: maxDisc }}
        role="application"
        aria-roledescription="Colour wheel"
        aria-label={`${name} grade colour. ${
          value.saturation === 0
            ? 'Neutral.'
            : `Hue ${Math.round(value.hue)} degrees, strength ${value.saturation} of 100.`
        } Arrow keys change hue and strength. Home clears this zone, and so does a double-click.`}
        tabIndex={0}
        onPointerDown={(event) => {
          // preventDefault stops the text-selection drag, so focus is moved by hand.
          event.preventDefault();
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
          applyFromPointer(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (!dragging) return;
          applyFromPointer(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          if (!dragging) return;
          setDragging(false);
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          onCommit();
        }}
        onPointerCancel={() => {
          if (!dragging) return;
          setDragging(false);
          onCommit();
        }}
        onDoubleClick={clearZone}
        onKeyDown={handleKeyDown}
        onKeyUp={(event) => {
          if (event.key.startsWith('Arrow')) onCommit();
        }}
      >
        <canvas
          ref={setCanvas}
          aria-hidden="true"
          style={{ display: 'block', width: '100%', height: '100%', borderRadius: '50%' }}
        />
        <span
          className="wheel-puck"
          style={{
            left: `${50 + Math.cos(angle) * reach}%`,
            top: `${50 - Math.sin(angle) * reach}%`,
            background: swatch(value.hue, value.saturation),
          }}
        />
      </div>

      {help ? (
        <Tip title={name} body={help} className="wheel-name">
          {name}
        </Tip>
      ) : (
        <span className="wheel-name">{name}</span>
      )}
      <div className="value" style={{ fontSize: 11 }} aria-live="polite">
        {readout}
      </div>

      <div style={{ width: '100%' }}>
        <Slider
          label={luminanceLabel}
          value={value.luminance}
          min={-100}
          max={100}
          step={1}
          neutral={0}
          help={luminanceHelp}
          onChange={(luminance) => onChange({ ...value, luminance })}
          onCommit={onCommit}
        />
      </div>
    </div>
  );
}

type TonalZone = Exclude<GradingZone, 'global'>;

const TONAL: { zone: TonalZone; name: string; short: string; help: string }[] = [
  {
    zone: 'shadows',
    name: 'Shadows',
    short: 'Shadows',
    help: 'Drag to tint the dark end of the picture. Double-click the disc to clear it.',
  },
  {
    zone: 'midtones',
    name: 'Midtones',
    short: 'Mids',
    help: 'Drag to tint the middle of the range, where skin usually sits.',
  },
  {
    zone: 'highlights',
    name: 'Highlights',
    short: 'Highs',
    help: 'Drag to tint the bright end. Warmth here reads like late sun.',
  },
];

const GLOBAL_HELP = 'Tints every tone at once, on top of the other three wheels.';
const ZONE_LIGHT_HELP = 'Lifts or drops brightness in this tone range only.';
const GLOBAL_LIGHT_HELP = 'Lifts or drops brightness across the whole frame.';

const ALL_ZONES: GradingZone[] = ['shadows', 'midtones', 'highlights', 'global'];

export function GradingWheels() {
  const photo = useSelectedPhoto();
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { width } = useElementSize(container);
  const [wide, setWide] = useState(false);
  const [zone, setZone] = useState<TonalZone>('shadows');

  useLayoutEffect(() => {
    if (width === 0) return;
    setWide((current) => width >= (current ? WIDE_MIN - WIDE_HYSTERESIS : WIDE_MIN));
  }, [width]);

  if (!photo) return null;
  const grading = photo.recipe.grading;

  const setWheel = (target: GradingZone, next: GradingWheel) => {
    updateRecipe((draft) => {
      draft.grading[target] = next;
    });
  };

  /** A hue with no strength behind it changes nothing, so it earns no marker. */
  const graded = (target: GradingZone) =>
    grading[target].saturation !== 0 || grading[target].luminance !== 0;

  const anyGraded = ALL_ZONES.some(
    (target) =>
      grading[target].hue !== 0 ||
      grading[target].saturation !== 0 ||
      grading[target].luminance !== 0,
  );

  const resetAll = () => {
    updateRecipe(
      (draft) => {
        for (const target of ALL_ZONES) {
          draft.grading[target] = { hue: 0, saturation: 0, luminance: 0 };
        }
      },
      { commit: true },
    );
  };

  const resetLink = anyGraded ? (
    <button type="button" className="link-btn" onClick={resetAll}>
      Reset wheels
    </button>
  ) : null;

  const globalWheel = (maxDisc: number) => (
    <ColorWheel
      name="Global"
      value={grading.global}
      help={GLOBAL_HELP}
      luminanceHelp={GLOBAL_LIGHT_HELP}
      maxDisc={maxDisc}
      onChange={(next) => setWheel('global', next)}
      onCommit={commit}
    />
  );

  const selected = TONAL.find((entry) => entry.zone === zone) ?? TONAL[0];

  return (
    <div ref={setContainer} role="group" aria-label="Colour grading wheels">
      {wide ? (
        <>
          <div className="wheels">
            {TONAL.map((entry) => (
              <ColorWheel
                key={entry.zone}
                name={entry.name}
                value={grading[entry.zone]}
                help={entry.help}
                luminanceHelp={ZONE_LIGHT_HELP}
                maxDisc={132}
                onChange={(next) => setWheel(entry.zone, next)}
                onCommit={commit}
              />
            ))}
          </div>

          <div className="wheels" style={{ marginTop: 12 }}>
            {globalWheel(132)}
            <div
              style={{
                gridColumn: 'span 2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {resetLink}
            </div>
          </div>
        </>
      ) : (
        <>
          <Segmented
            value={zone}
            options={TONAL.map((entry) => ({
              value: entry.zone,
              label: graded(entry.zone) ? `${entry.short} •` : entry.short,
            }))}
            onChange={setZone}
            ariaLabel="Grading zone"
          />

          <div style={{ marginTop: 10 }}>
            <ColorWheel
              key={selected.zone}
              name={selected.name}
              value={grading[selected.zone]}
              help={selected.help}
              luminanceHelp={ZONE_LIGHT_HELP}
              maxDisc={200}
              onChange={(next) => setWheel(selected.zone, next)}
              onCommit={commit}
            />
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 104, flex: '0 0 auto' }}>{globalWheel(104)}</div>
            <div style={{ flex: 1 }}>{resetLink}</div>
          </div>
        </>
      )}
    </div>
  );
}
